import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BoRolesCheckService } from '../../../../components/rolesCheck/bo-roles-check.service';
import { GlobalDataService } from '../../../../services/global-data.service';
import { AWSLambda } from 'src/app/AWSLambda.service';

@Component({
  selector: 'app-bo-esim-gift',
  templateUrl: './bo-esim-gift.component.html',
  styleUrls: ['./bo-esim-gift.component.scss']
})
export class BOEsimGiftComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private rolesCheck = inject(BoRolesCheckService);
  private globalDataService = inject(GlobalDataService);
  private lambda = inject(AWSLambda);

  canAccess = false;
  loading = true;
  showSpinner = false;

  // Usuario destino del regalo
  targetUserId: string = '';
  targetUserName: string = '';
  targetUserEmail: string = '';

  // Selectores
  types: string[] = ['Local', 'Regional', 'Global'];
  selectedType: string = '';
  selectedCountry: string = '';

  countries: any[] = [];
  filteredCountries: any[] = [];
  packagesByCountry: { country: string, code: string, image: string, packages: any[] }[] = [];
  filteredPackages: any[] = [];

  isTypeSelected = false;
  isCountrySelected = false;
  showPackages = false;
  countryName = '';

  async ngOnInit() {
    this.canAccess = await this.rolesCheck.checkAccess();
    if (!this.canAccess) {
      return;
    }

    // Obtener userId de la URL
    this.targetUserId = this.route.snapshot.paramMap.get('userId') || '';

    if (!this.targetUserId) {
      this.snackBar.open('No se ha especificado el usuario destino', 'Cerrar', { duration: 5000 });
      this.router.navigate(['/backoffice/users']);
      return;
    }

    // Cargar datos del usuario destino
    await this.loadTargetUserDetails();
    this.loading = false;
  }

  async loadTargetUserDetails() {
    try {
      const data = await this.lambda.getLambdaFunction('lambda', '/get/userDetails', {
        queryStringParameters: { sub: this.targetUserId }
      });

      this.targetUserName = [data?.name, data?.middle_name].filter(Boolean).join(' ') || 'Usuario';
      this.targetUserEmail = data?.email || '';
    } catch (error) {
      console.error('Error cargando datos del usuario:', error);
      this.snackBar.open('Error al cargar datos del usuario', 'Cerrar', { duration: 5000 });
    }
  }

  async onTypeChange() {
    this.showSpinner = true;
    this.filteredCountries = [];
    this.selectedCountry = '';
    this.filteredPackages = [];
    this.showPackages = false;

    try {
      const apiPackages = this.globalDataService.getEsimFilteredCountries(this.selectedType);

      if (!apiPackages) {
        console.error('Response is undefined or empty');
        return;
      }

      const apiResponse = typeof apiPackages === 'string' ? JSON.parse(apiPackages) : apiPackages;
      const packagesMap: { [key: string]: { country: string, image: string, packages: any[] } } = {};
      this.packagesByCountry = [];

      apiResponse.forEach((pkg: any) => {
        if (pkg.pkg_country_code) {
          if (!packagesMap[pkg.pkg_country_code]) {
            packagesMap[pkg.pkg_country_code] = {
              country: pkg.pkg_country,
              image: pkg.pkg_country_url || 'https://cdn-dev.zendy.tel/images/Promo-Zendy-3-24@3x.png',
              packages: []
            };

            this.packagesByCountry.push({
              country: pkg.pkg_country_title,
              code: pkg.pkg_country_code,
              image: pkg.pkg_country_url,
              packages: []
            });
          }

          const type = pkg.pkg_type || 'Local';
          packagesMap[pkg.pkg_country_code].packages.push({
            country: pkg.pkg_country,
            country_code: pkg.pkg_country_code,
            countryimg: packagesMap[pkg.pkg_country_code].image,
            voice: pkg.pkg_voice,
            amount: pkg.pkg_amount,
            data: pkg.pkg_data,
            net_price: pkg.pkg_price,
            type: type,
            title: pkg.pkg_title,
            is_unlimited: pkg.pkg_is_unlimited,
            price: pkg.pkg_price,
            id: pkg.pkg_id,
            text: pkg.pkg_text,
            day: pkg.pkg_day,
            operador: [{
              operador: pkg.operador_title,
              type: pkg.operador_plan_type,
              prepaid: pkg.pkg_is_prepaid === 'true',
              esimtype: pkg.operador_apn_type,
              apn_type: pkg.operador_apn_type,
              apn_value: pkg.operador_apn_value,
              is_roaming: pkg.operador_is_roaming,
              info: pkg.operador_info,
              image: pkg.operador_url || 'https://cdn-dev.zendy.tel/images/Promo-Zendy-3-24@3x.png',
              plan_type: pkg.operador_plan_type,
              activation_policy: pkg.operador_activation_policy,
              rechargeability: pkg.operador_rechargeability,
              other_info: pkg.operador_other_info
            }]
          });
        }
      });

      this.packagesByCountry.forEach(pkg => {
        pkg.packages = packagesMap[pkg.code]?.packages || [];
      });

      this.packagesByCountry.sort((a, b) => a.country.localeCompare(b.country));

      // Filtrar países por tipo seleccionado
      this.packagesByCountry.forEach(country => {
        const filteredPkgs = country.packages.filter(pkg => pkg.type === this.selectedType);
        if (filteredPkgs.length > 0) {
          this.filteredCountries.push({ ...country, packages: filteredPkgs });
        }
      });

      this.isTypeSelected = true;
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      this.showSpinner = false;
    }
  }

  onCountryChange() {
    this.isCountrySelected = !!this.selectedCountry;
  }

  loadPackages() {
    if (!this.selectedCountry) {
      this.snackBar.open('Selecciona un país', 'Cerrar', { duration: 3000 });
      return;
    }

    const selectedCountryData = this.packagesByCountry.find(pkg => pkg.code === this.selectedCountry);
    this.filteredPackages = selectedCountryData ? selectedCountryData.packages : [];
    this.filteredPackages.sort((a, b) => a.price - b.price);

    const countryData = this.filteredCountries.find(c => c.code === this.selectedCountry);
    this.countryName = countryData?.country || '';
    this.showPackages = true;
  }

  selectPackage(pkg: any) {
    // Navegar a los detalles del paquete pasando el packageId
    this.router.navigate(['/backoffice/users', this.targetUserId, 'gift-esim', pkg.id], {
      state: { packageData: pkg }
    });
  }

  goBack() {
    if (this.showPackages) {
      this.showPackages = false;
      this.filteredPackages = [];
    } else {
      this.router.navigate(['/backoffice/users', this.targetUserId]);
    }
  }

  capitalizeCountryName(country: string): string {
    return country
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
